import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router";
import { Authenticated, useNotify } from "ra-core";
import { AlertCircle, Download, Link2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FileTypeIcon } from "@/lib/fileAttachments";
import {
  blobForInlinePreview,
  downloadPrivateStorageFile,
  fetchPrivateStorageBlob,
  isPdfPreviewName,
} from "@/lib/supabase/privateStorageFile";

type Preview =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; url: string; kind: "pdf" | "image" | "video" | "other" };

const previewKind = (blob: Blob, filename: string) => {
  if (blob.type.includes("pdf") || isPdfPreviewName(filename)) return "pdf";
  if (blob.type.startsWith("image/")) return "image";
  if (blob.type.startsWith("video/")) return "video";
  return "other";
};

const FileViewer = () => {
  const notify = useNotify();
  const { bucket = "", "*": path = "" } = useParams();
  const { search } = useLocation();
  const filename =
    new URLSearchParams(search).get("name") || path.split("/").pop() || "file";
  const [preview, setPreview] = useState<Preview>({ status: "loading" });

  useEffect(() => {
    document.title = filename;
  }, [filename]);

  useEffect(() => {
    let url: string | undefined;
    let cancelled = false;
    setPreview({ status: "loading" });
    fetchPrivateStorageBlob({ bucket, path, filename })
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blobForInlinePreview(blob, filename));
        setPreview({ status: "ready", url, kind: previewKind(blob, filename) });
      })
      .catch(() => {
        if (!cancelled) setPreview({ status: "error" });
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [bucket, path, filename]);

  const download = () =>
    downloadPrivateStorageFile({ bucket, path, filename }).catch(() =>
      notify("Could not download the file", { type: "error" }),
    );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notify("Link copied · teammates need to be signed in to Nomi", {
        type: "info",
      });
    } catch {
      notify("Could not copy the link", { type: "error" });
    }
  };

  return (
    <div className="flex h-dvh flex-col bg-muted/40">
      <header className="flex shrink-0 items-center gap-3 border-b bg-background px-4 py-2">
        <FileTypeIcon file={{ title: filename }} className="size-5" />
        <h1 className="min-w-0 flex-1 truncate text-sm font-medium">
          {filename}
        </h1>
        <Button type="button" variant="ghost" size="sm" onClick={copyLink}>
          <Link2 className="size-4" />
          Copy link
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={download}>
          <Download className="size-4" />
          Download
        </Button>
      </header>

      <main className="min-h-0 flex-1">
        {preview.status === "loading" ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Opening {filename}…
          </div>
        ) : preview.status === "error" ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
            <AlertCircle className="size-6" />
            <p>This file could not be opened. It may have been removed.</p>
          </div>
        ) : preview.kind === "pdf" ? (
          <iframe
            src={preview.url}
            title={filename}
            className="size-full border-0"
          />
        ) : preview.kind === "image" ? (
          <div className="flex h-full items-center justify-center overflow-auto p-6">
            <img
              src={preview.url}
              alt={filename}
              className="max-h-full max-w-full rounded-md shadow-sm"
            />
          </div>
        ) : preview.kind === "video" ? (
          <div className="flex h-full items-center justify-center p-6">
            <video
              src={preview.url}
              controls
              className="max-h-full max-w-full"
            />
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
            <FileTypeIcon file={{ title: filename }} className="size-10" />
            <p className="text-sm text-muted-foreground">
              No preview for this file type.
            </p>
            <Button type="button" onClick={download}>
              <Download className="size-4" />
              Download {filename}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

/** Full-page viewer for a private storage file at a real Nomi address. */
export const PrivateFileViewerPage = () => (
  <Authenticated>
    <FileViewer />
  </Authenticated>
);
